//-- Copyright (c) 2021 TMT Insights Group, LLC
import { OrderPlacementConfig } from '@tmt-insights/oms_fe_core';
import { Order, OrderJob, TitleEntity } from '@/redux/slices/slice_types';
import styled from '@emotion/styled';
import { Grid, Tooltip, Typography } from '@tmt-insights/oms_fe_core/material';
import { buildTitleURL, getTitlePath } from '@tmt-insights/oms_fe_core/titleUtils';
import {
  BoolIcon,
  DateTimeDisplay,
  FileType,
  JobTypeLabel,
  LabelValue,
  PolarisIcon,
  Thumbnail,
  TitleDetailsPageLink,
  TitleTypeLabel,
  TitleVersionChip,
  UserDisplay
} from '@tmt-insights/oms_fe_core/columns';

export const getLineageMetaTitle_bbc = (title: Partial<TitleEntity> = {}, suppressEpisodeName = false) => {
  const meta = title?.lineage_meta || {};
  if (!Object.keys(meta).length) return '';

  const toValidNumber = (value: unknown): number => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const entityType = title.og_entity_type || title.entity_type;
  const seriesName = meta.series_name || '';
  const seasonNum = meta.season_sequence_order ? `S${meta.season_sequence_order}` : '';
  const programSeq = meta.program_sequence_order;
  const episodeSeq = meta.episode_sequence_order;
  const titleSeq = title.sequence_order;
  const epNum = Math.max(toValidNumber(programSeq), toValidNumber(titleSeq), toValidNumber(episodeSeq));
  const epNumDisplay = epNum ? `E${isNaN(Number(epNum)) ? epNum : Number(epNum)}` : '';
  const episodeName = meta.episode_name || meta.program_name || title.name;
  const isEpisode = (entityType && ['program', 'episode'].includes(entityType)) || programSeq || meta.episode_name;
  const isMovie = !!meta.movie_name;
  const isOVA = !!meta.ova_name;
  const isFeature = !!meta.feature_name;

  if (isEpisode || meta?.parent_entity_type === 'episode') {
    const episodeTitle = suppressEpisodeName ? '' : `: ${episodeName}`;
    return [seriesName, seasonNum, epNumDisplay].filter(Boolean).join(' ') + episodeTitle;
  }
  if (isMovie) {
    return [seriesName, meta.movie_name].filter(Boolean).join(' ');
  }
  if (isOVA) {
    return [seriesName, meta.ova_name].filter(Boolean).join(' ');
  }
  if (isFeature) {
    return meta.feature_name;
  }
  // Default fallback
  const seriesTitle = seriesName ? `: ${seriesName}` : '';
  return [seasonNum, epNumDisplay].filter(Boolean).join('') + seriesTitle;
};

const bulkTitleSearchOptions = new Map<string, string>([
  ['RLID', 'Version RLID'],
  ['P7', 'Version P7ID'],
  ['MCID', 'Version MCID']
]);

const orderPlacementConfig: OrderPlacementConfig = {
  bulkTitleSearchOptions,
  entityTypes: ['feature', 'series', 'episode'],
  skipRallyIdCheck: true,
  // v3 is still in progress - don't hardcode it here while this ships to BBC's real users on main.
  // Reach it with ?opv3=1 (see useOrderPlacementVersion in orderPlacementVersion.ts) instead, which
  // sticks for that browser without touching what any client, including BBC, gets by default.

  viewingTitleHeader: (title: TitleEntity) => {
    const lineageMetaTitle = getLineageMetaTitle_bbc(title, true);
    return (
      <>
        <Typography variant="h6" sx={{ mb: 0 }}>
          <strong>{lineageMetaTitle || title.name}</strong>
        </Typography>
        <Typography variant="h6" sx={{ mb: 0, opacity: 0.7 }}>
          {lineageMetaTitle ? title.name : ''}
        </Typography>
        <Grid container justifyContent="space-between" alignItems="flex-end">
          <div>
            <Grid container sx={{ mb: 1 }}>
              <TitleTypeLabel type={title.entity_type} />
            </Grid>
          </div>
        </Grid>
      </>
    );
  },
  versionDropdownOption: (option: TitleEntity) => {
    if (!option.external_id) return option.name;
    return (
      <Grid container alignItems="center">
        {option.name}
        <Typography sx={{ opacity: 0.5, ml: 1, fontSize: '.9rem', fontFamily: 'DM Sans', fontWeight: 500 }}>[{option.external_id}]</Typography>
      </Grid>
    );
  },
  versionOptionLabel: (option: TitleEntity) => {
    if (!option) return null;
    return `${option.name} ${option.external_id ? `[${option.external_id}]` : ''}`;
  },
  titleSearchResult: (result: TitleEntity) => {
    const series = result?.lineage_meta?.series_name;
    const seasonNum = series ? result?.lineage_meta?.season_sequence_order : null;
    const episodeNum = result.entity_type === 'episode' || result?.lineage_meta?.parent_entity_type === 'episode' ? result?.sequence_order : null;

    return (
      <Grid container>
        <Thumbnail src={result?.thumbnail_link || ''} />
        <Grid item sx={{ ml: 1.5 }}>
          <div>
            <Typography sx={{ fontWeight: 'bold', fontSize: '1.1rem', mb: 0 }}>{result.name}</Typography>
            <Typography variant="caption">
              <span>
                {series ? `${series} ` : ''}
                {seasonNum ? `S${seasonNum} ` : ''}
                {episodeNum ? `E${isNaN(Number(episodeNum)) ? episodeNum : Number(episodeNum)}` : ''}
              </span>
            </Typography>
          </div>
        </Grid>
        <Grid item sx={{ ml: 'auto', textAlign: 'right' }}>
          <TitleTypeLabel width="auto" type={result?.entity_type} sx={{ ml: 'auto' }} />
        </Grid>
      </Grid>
    );
  },
  dueDateOffsetDays: 7, // 7 days into the future
  bulkOrderMetadata: (order: Order) => {
    const fileType = order?.draft_state?.file?.name?.split('.').pop();
    return (
      <>
        <LabelValue label="Order ID">{order.hid}</LabelValue>
        <LabelValue label="Description">
          <span style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
            <PolarisIcon
              icon="bulk"
              height={15}
              width={15}
              style={{
                marginRight: '8px',
                color: window.theme.palette.secondary.main
              }}
            />
            {order.description}
          </span>
        </LabelValue>
        <LabelValue label="Managed by">
          <UserDisplay user={order?.managed_by_user} />
        </LabelValue>
        <LabelValue label="Billable Customer">{order.billable_customer}</LabelValue>
        <LabelValue label="Billing Code">{order.billing_code}</LabelValue>
        <LabelValue label="Created">
          <DateTimeDisplay singleLine>{order?.created_at}</DateTimeDisplay>
        </LabelValue>
        <LabelValue label="Customer">{order.draft_state?.profileData?.customer}</LabelValue>
        <LabelValue label="Profile">
          <a
            href={`/profiles/${order.draft_state?.profileData?.id}`}
            target="_blank"
            rel="noreferrer"
            style={{
              textDecoration: 'none',
              cursor: 'pointer',
              color: window.theme.palette.primary.main
            }}
          >
            {order.draft_state?.profileData.label}
          </a>
        </LabelValue>
        {fileType && (
          <LabelValue label="Imported File Name">
            <Grid container alignItems="center">
              <FileType type={fileType} height={20} style={{ marginRight: 5 }} />
              {order?.draft_state?.file?.name}
            </Grid>
          </LabelValue>
        )}
      </>
    );
  },
  getLineageMetaTitle: getLineageMetaTitle_bbc,
  bulkOrderJobDisplay: (job: OrderJob) => {
    const isEmbargoedTitle = Boolean(job.is_embargoed || job.version?.is_embargoed);

    const DefaultWrapper = styled.div`
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: flex-start;
      column-gap: ${({ theme }) => theme.spacing(2)};
    `;

    return (
      <DefaultWrapper>
        <Typography variant="h2" sx={{ fontSize: '1.2rem', fontWeight: 'normal' }}>
          <strong>{job.hid}</strong>
        </Typography>
        <JobTypeLabel type={job.job_type} />
        <Typography sx={{ display: 'flex', alignItems: 'center', columnGap: 1 }}>
          {job.description}
          {isEmbargoedTitle && (
            <Tooltip title="Embargoed">
              <span style={{ color: 'var(--warning-main)', height: 20 }}>
                <PolarisIcon icon="error" height={20} />
              </span>
            </Tooltip>
          )}
        </Typography>
        <Grid container sx={{ ml: 'auto' }} item sm={2.5}>
          <LabelValue label="On Hold">
            <DateTimeDisplay missingText="-" singleLine>
              {job.embargo_date}
            </DateTimeDisplay>
          </LabelValue>
          <LabelValue label="Due Date">
            <DateTimeDisplay missingText="-" singleLine>
              {job.due_date}
            </DateTimeDisplay>
          </LabelValue>
        </Grid>
      </DefaultWrapper>
    );
  },
  versionChipTooltip: (version: TitleEntity, tooltipSource?: string) => {
    let versionText = version.external_id || version.id;

    if (tooltipSource === 'global-channel-ids') {
      if (version.rightsline_id) {
        versionText += ` | ${version.rightsline_id}`;
      }

      if (version.phoenix7_id) {
        versionText += ` | ${version.phoenix7_id}`;
      }
    } else if (tooltipSource === 'name') {
      versionText = version.name;
    }

    return versionText;
  },
  parseVersionsFn: (response, params) => {
    const searchValue = params?.titleSearchValue?.toLowerCase()?.trim();
    if (!searchValue) return response;

    const keys = ['phoenix7_id', 'rightsline_id', 'external_id'];
    const matches = (version: TitleEntity) => keys.some((key) => version[key]?.toLowerCase() === searchValue);

    return response?.some(matches) ? response.filter(matches) : response;
  },
  expandedVersionRows: (displayedVersions, searchedVersion, _isTitleV2, _bussinessUnit, params) => {
    let versions = (searchedVersion && searchedVersion.length ? searchedVersion : displayedVersions) as TitleEntity[];

    // NOTE(MSC-1499): a withdrawn version is selectable only by a user the API says may
    // order it - can_order_withdrawn is computed per-user by the title serializers, so the
    // permission rule lives in one place rather than being reimplemented here.
    versions = versions.map((version) => ({
      ...version,
      disabled: (version.is_withdrawn && !version.can_order_withdrawn) || version.disabled
    }));

    if (!params.canCreateEmbargoedTitleOrderPending && !params.canCreateEmbargoedTitleOrderProgress) {
      versions = versions.map((version) => ({
        ...version,
        disabled: version.is_embargoed || version.disabled
      }));
    }

    const icon = <PolarisIcon icon="external_link" height={18} />;

    const columns: { id: string; label: string; size: number; link?: boolean; alt?: string; bool?: boolean }[] = [
      {
        id: 'external_id',
        label: 'Version MCID',
        size: 2,
        link: true
      },
      {
        id: 'rightsline_id',
        label: 'Version RLID',
        size: 2
      },
      {
        id: 'phoenix7_id',
        label: 'Version P7 ID',
        size: 2
      },
      {
        id: 'is_withdrawn',
        label: 'Withdrawn',
        size: 2,
        bool: true
      },
      {
        id: 'is_embargoed',
        label: 'Embargo Status',
        size: 2,
        bool: true
      },
      {
        id: 'action',
        label: '',
        size: 2
      }
    ];
    return (
      <Grid container sx={{ px: 1 }}>
        <HeaderRow container>
          {columns.map((column) => (
            <Cell header={1} key={column.id} item xs={column.size} center={+!!column.bool}>
              {column.label}
            </Cell>
          ))}
        </HeaderRow>
        <Content container>
          {versions.map((version) => (
            <Row key={version.hid} container>
              {columns.map((column) => {
                let value = version[column.id];
                if (!value && column.alt) {
                  value = version[column.alt];
                }
                const isWithdrawn = version.is_withdrawn && !version.can_order_withdrawn;
                const isEmbargoed = !isWithdrawn && version.is_embargoed && version.disabled;
                const chipTooltip = isWithdrawn
                  ? 'This version has been withdrawn\n and cannot be ordered'
                  : isEmbargoed
                    ? 'This version is embargoed\n and cannot be ordered'
                    : undefined;

                return (
                  <Cell key={column.id} item xs={column.size} right={+(column.id === 'action')} center={+!!column.bool}>
                    {column.id === 'action' ? (
                      <TitleVersionChip tooltip={chipTooltip} tooltipSource="name" withTooltip version={version} />
                    ) : column.bool ? (
                      <BoolIcon checked={!!version[column.id]} />
                    ) : (
                      <>
                        <span>{value || '-'}</span>
                        {column.link && (
                          <TitleDetailsPageLink url={buildTitleURL({ path: getTitlePath(version), version: version.id })}>
                            {icon}
                          </TitleDetailsPageLink>
                        )}
                      </>
                    )}
                  </Cell>
                );
              })}
            </Row>
          ))}
        </Content>
      </Grid>
    );
  },
  searchTermFilter: (versions: TitleEntity[], searchTerm: string) =>
    versions?.filter((v) => v.rightsline_id?.toLowerCase()?.includes(searchTerm?.toLowerCase()?.trim())),
  addlFields: {
    orderDetails: {
      cost_centre: {
        label: 'Cost Centre',
        required: true
      },
      opportunity_id: {
        label: 'Opportunity ID'
      }
      // department_charge_code: {
      //   label: 'Department Charge Code'
      // },
    },
    profileDetails: {
      assignee_user_id: {
        label: 'Assign To'
      }
    }
  },
  checkEmbargoedVersions: true,
  checkWithdrawnVersions: true,
  disableAddVersionFn: (_entity: TitleEntity, _hierarchy, params: any) => {
    // NOTE(MSC-1499): see expandedVersionRows - can_order_withdrawn is per-user.
    if (params?.version?.is_withdrawn && !params?.version?.can_order_withdrawn) return true;

    if (params.canCreateEmbargoedTitleOrderPending || params.canCreateEmbargoedTitleOrderProgress) {
      return params?.version?.disabled;
    }

    return params?.version?.is_embargoed || params?.version?.disabled;
  },
  calculateInitialVersion: (versions: TitleEntity[], search: string) => {
    const searchTerm = search?.toLowerCase()?.trim();
    const match = versions?.find(
      (v) =>
        v.external_id?.toString()?.toLowerCase() === searchTerm ||
        v.rightsline_id?.toString()?.toLowerCase() === searchTerm ||
        v.phoenix7_id?.toLowerCase() === searchTerm
    );
    return match?.name ?? versions?.[0]?.name;
  },
  showFileTitleLabel: true
};

export default orderPlacementConfig;

const HeaderRow = styled(Grid)`
  border-bottom: 1px solid ${({ theme }) => theme.palette.divider};
  padding-bottom: ${({ theme }) => theme.spacing(1)};
`;

const Cell = styled(Grid)<{ right?: number; header?: number; center?: number }>`
  display: flex;
  justify-content: ${({ right, center }) => (right ? 'flex-end' : center ? 'center' : 'flex-start')};
  opacity: ${({ header }) => (header ? 0.5 : 1)};
  align-items: center;
  column-gap: ${({ theme }) => theme.spacing(1)};
`;

const Content = styled(Grid)`
  margin-top: ${({ theme }) => theme.spacing(1)};
`;

const Row = styled(Grid)`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: ${({ theme }) => theme.spacing(1)};

  & .link {
    transition: opacity 0.2s ease-in-out;
    opacity: 0;
  }

  &:hover {
    background-color: ${({ theme }) => theme.palette.action.hover};
    & .link {
      opacity: 1;
    }
  }
`;
